/**
 * AXEL AssetChart v1 — TradingView Advanced Chart (free tv.js widget) inside
 * the AXEL Intel Hub. Left: chart. Right: Signal Ladder reading the active
 * signal from IntelHubContext. Chart follows the active signal symbol
 * (one-way sync); symbol/timeframe pills are the source of truth for manual
 * switching. Clean remount via key={symbol + "-" + interval} — no innerHTML
 * mutation, so no double-injection.
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useIntelHub } from "../context/IntelHubContext";

const T = {
  bg: "#07070f", surf: "#0d1117", border: "#1c2030", gold: "#C9A227",
  green: "#00d68f", red: "#ff4757", text: "#c8d4e0", muted: "#4a5568", white: "#eef2f7",
};

const SYMBOLS = [
  { label: "XAUUSD", tv: "OANDA:XAUUSD" },
  { label: "BTCUSD", tv: "BINANCE:BTCUSDT" },
  { label: "EURUSD", tv: "OANDA:EURUSD" },
  { label: "GBPUSD", tv: "OANDA:GBPUSD" },
  { label: "US100", tv: "CAPITALCOM:US100" },
];
const TIMEFRAMES = [
  { label: "15m", v: "15" }, { label: "1H", v: "60" },
  { label: "4H", v: "240" }, { label: "1D", v: "D" },
];
const TV_SRC = "https://s3.tradingview.com/tv.js";

// Load tv.js exactly once. Callback fires when window.TradingView is ready.
function loadTradingView(cb) {
  if (window.TradingView) { cb(); return; }
  let s = document.getElementById("flexx-tv-js");
  if (!s) {
    s = document.createElement("script");
    s.id = "flexx-tv-js";
    s.src = TV_SRC;
    s.async = true;
    document.head.appendChild(s);
  }
  if (window.TradingView) { cb(); return; }
  s.addEventListener("load", cb, { once: true });
}

function useIsMobile() {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onR = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return m;
}

// One TradingView widget instance. Parent remounts this via `key` so the
// container id is always fresh — the recommended clean-recreation pattern.
function TVWidget({ tvSymbol, interval, height }) {
  const idRef = useRef(`flexx_tv_${Math.random().toString(36).slice(2)}`);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadTradingView(() => {
      if (cancelled || !window.TradingView || !document.getElementById(idRef.current)) return;
      new window.TradingView.widget({
        autosize: true,
        symbol: tvSymbol,
        interval,
        timezone: "Etc/UTC",
        theme: "dark",
        style: "1",
        locale: "en",
        hide_side_toolbar: false,
        allow_symbol_change: false,
        container_id: idRef.current,
      });
      setReady(true);
    });
    return () => { cancelled = true; };
  }, [tvSymbol, interval]);

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      {!ready && (
        <div data-testid="assetchart-skeleton" style={{
          position: "absolute", inset: 0, borderRadius: 10, zIndex: 2,
          background: "linear-gradient(100deg, #0d1117 30%, #141a26 50%, #0d1117 70%)",
          backgroundSize: "200% 100%", animation: "flexxShimmer 1.4s ease-in-out infinite",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: T.muted, fontFamily: "Barlow, sans-serif", fontSize: ".78rem",
        }}>
          Loading {tvSymbol} chart…
        </div>
      )}
      <div id={idRef.current} style={{ width: "100%", height: "100%" }} />
      <style>{"@keyframes flexxShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}"}</style>
    </div>
  );
}

const Pill = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick} style={{
    padding: "6px 14px", borderRadius: 999, cursor: "pointer",
    fontFamily: "Barlow Condensed, sans-serif", fontSize: ".8rem", fontWeight: 700,
    letterSpacing: ".04em", transition: "background-color .18s, color .18s, border-color .18s",
    background: active ? T.gold : "transparent",
    border: `1px solid ${active ? T.gold : T.border}`,
    color: active ? "#07070f" : T.text,
  }}>{children}</button>
);

function LadderRow({ label, value, color, strong }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "9px 12px", borderRadius: 8, background: T.surf,
      border: `1px solid ${T.border}`, borderLeft: `3px solid ${color}`,
    }}>
      <span style={{ fontSize: ".64rem", letterSpacing: ".1em", textTransform: "uppercase",
        color: T.muted, fontFamily: "Barlow Condensed, sans-serif" }}>{label}</span>
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: strong ? ".95rem" : ".85rem",
        fontWeight: strong ? 800 : 600, color }}>{value ?? "—"}</span>
    </div>
  );
}

function SignalLadder({ signal }) {
  if (!signal) {
    return (
      <div data-testid="assetchart-ladder-empty" style={{
        border: `1px dashed ${T.border}`, borderRadius: 10, padding: "28px 18px",
        textAlign: "center", color: T.muted, fontFamily: "Barlow, sans-serif", fontSize: ".8rem", lineHeight: 1.7,
      }}>
        <div style={{ fontSize: "1.5rem", marginBottom: 8 }}>📡</div>
        No active signal — run <b style={{ color: T.text }}>Live Trigger</b> or <b style={{ color: T.text }}>MT5 Setup</b>.
      </div>
    );
  }
  const dir = (signal.direction || "").toUpperCase();
  const dirColor = dir === "BUY" ? T.green : dir === "SELL" ? T.red : T.muted;
  return (
    <div data-testid="assetchart-ladder" style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
        <span style={{ fontFamily: "Barlow Condensed, sans-serif", fontWeight: 700, color: T.white, letterSpacing: ".04em" }}>
          {signal.symbol} Signal
        </span>
        <span data-testid="assetchart-ladder-direction" style={{
          fontFamily: "Barlow Condensed, sans-serif", fontWeight: 800, fontSize: ".78rem",
          color: dirColor, border: `1px solid ${dirColor}`, borderRadius: 5, padding: "2px 10px", letterSpacing: ".08em",
        }}>{dir || "—"}</span>
      </div>
      <LadderRow label="TP3" value={signal.tp3} color={T.green} />
      <LadderRow label="TP2" value={signal.tp2} color={T.green} />
      <LadderRow label="TP1" value={signal.tp1} color={T.green} />
      <LadderRow label="Entry" value={signal.entry} color={T.gold} strong />
      <LadderRow label="Stop Loss" value={signal.stop_loss} color={T.red} />
      {signal.source && (
        <div style={{ fontSize: ".62rem", color: T.muted, fontFamily: "Barlow, sans-serif", marginTop: 2 }}>
          Source: {signal.source === "live_trigger" ? "AXEL Live Trigger" : "MT5 Setup"}
        </div>
      )}
    </div>
  );
}

export default function AXELAssetChart() {
  const { activeSignal, chartSymbol } = useIntelHub();
  const isMobile = useIsMobile();
  const [selected, setSelected] = useState(chartSymbol || "XAUUSD");
  const [interval, setInterval] = useState("60");

  // One-way sync: chart follows the active signal's symbol. Never the reverse.
  useEffect(() => {
    if (chartSymbol && SYMBOLS.some(s => s.label === chartSymbol)) {
      setSelected(chartSymbol);
    }
  }, [chartSymbol]);

  const tvSymbol = useMemo(
    () => (SYMBOLS.find(s => s.label === selected) || SYMBOLS[0]).tv, [selected]);
  const height = isMobile ? 360 : 480;

  return (
    <div data-testid="assetchart-tab" style={{ maxWidth: 1180, margin: "0 auto", padding: "0 14px 80px" }}>
      {/* Controls */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 14, justifyContent: "space-between" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }} data-testid="assetchart-symbol-pills">
          {SYMBOLS.map(s => (
            <Pill key={s.label} active={selected === s.label} onClick={() => setSelected(s.label)} testId={`assetchart-symbol-${s.label}`}>
              {s.label}
            </Pill>
          ))}
        </div>
        <div style={{ display: "flex", gap: 7 }} data-testid="assetchart-tf-pills">
          {TIMEFRAMES.map(tf => (
            <Pill key={tf.v} active={interval === tf.v} onClick={() => setInterval(tf.v)} testId={`assetchart-tf-${tf.v}`}>
              {tf.label}
            </Pill>
          ))}
        </div>
      </div>

      {/* Two-column layout — stacked on mobile */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 16, alignItems: "stretch" }}>
        {/* Chart card */}
        <div style={{ flex: isMobile ? "1 1 auto" : "0 0 70%", maxWidth: isMobile ? "100%" : "70%",
          background: T.bg, border: `1px solid ${T.border}`, borderRadius: 14, padding: 12,
          boxShadow: "0 8px 30px rgba(0,0,0,.35)" }} data-testid="assetchart-chart-card">
          {/* key => full unmount/remount on symbol or interval change */}
          <div key={`${selected}-${interval}`} style={{ borderRadius: 10, overflow: "hidden" }}>
            <TVWidget tvSymbol={tvSymbol} interval={interval} height={height} />
          </div>
          <div style={{ textAlign: "right", marginTop: 6 }}>
            <a href="https://www.tradingview.com/" rel="noopener noreferrer" target="_blank"
              style={{ fontSize: ".6rem", color: T.muted, textDecoration: "none", fontFamily: "Barlow, sans-serif" }}>
              Chart by <span style={{ color: T.gold }}>TradingView</span>
            </a>
          </div>
        </div>

        {/* Signal Ladder */}
        <div style={{ flex: "1 1 auto", background: T.bg, border: `1px solid ${T.border}`,
          borderRadius: 14, padding: 16 }} data-testid="assetchart-ladder-panel">
          <div style={{ fontSize: ".62rem", color: T.gold, letterSpacing: ".14em", textTransform: "uppercase",
            marginBottom: 12, fontFamily: "Barlow Condensed, sans-serif" }}>AXEL Signal Ladder</div>
          <SignalLadder signal={activeSignal} />
        </div>
      </div>
    </div>
  );
}
